import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Alert, Anchor, Avatar, Button, Card, Col, Collapse, List, message, Modal, Row, Space, Statistic, Table, Tag, Spin, Typography,
    Form, Input, InputNumber, Tooltip
} from 'antd';
import {
    AimOutlined, AppstoreAddOutlined, BookOutlined, BuildOutlined, BulbOutlined,
    CheckCircleOutlined, ClockCircleOutlined, FileTextOutlined, InfoCircleOutlined,
    TeamOutlined, UserOutlined, UnorderedListOutlined, MessageOutlined,
    WarningOutlined, CheckSquareOutlined, ToolOutlined,
    DeleteOutlined, EditOutlined, PlusOutlined
} from '@ant-design/icons';
import EditableSection from './EditableSection';
import { getIcon } from './utils/iconMap';
import * as lessonPlanApi from './services/lessonPlanApi';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;


const updateNestedState = (obj, path, value) => {
    const newObj = JSON.parse(JSON.stringify(obj)); // Deep copy
    const keys = path.split('.').map(key => isNaN(parseInt(key, 10)) ? key : parseInt(key, 10));
    let current = newObj;
    for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
        if (current === undefined) return obj; // Path does not exist
    }
    current[keys[keys.length - 1]] = value;
    return newObj;
};

const formatConfigKey = (key) => {
    if (typeof key !== 'string') return key;
    return key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
};


const InteractiveLessonPlan = ({
                                   formValues,
                                   lessonContent: generatedPlan,
                                   instructionalBlocks = [],
                                   contentTypes = {},
                                   editingLessonId
                               }) => {
    const [editingState, setEditingState] = useState({ key: null, content: '' });
    const [adaptedKeys, setAdaptedKeys] = useState(new Set());
    const [isBlockLibraryVisible, setIsBlockLibraryVisible] = useState(false);
    const [insertionIndex, setInsertionIndex] = useState(null);
    const [lessonContent, setLessonContent] = useState(null);

    const [isSegmentModalVisible, setIsSegmentModalVisible] = useState(false);
    const [editingSegmentIndex, setEditingSegmentIndex] = useState(null);
    const [segmentForm] = Form.useForm();

    const getScrollContainer = () => {
        return document.getElementById('contentPanel');
    };


    useEffect(() => {
        if (generatedPlan) {
            setLessonContent(generatedPlan);
            setAdaptedKeys(new Set());
        }
    }, [generatedPlan]);

    const allContentTypes = useMemo(() => {
        return [
            ...(contentTypes.instructional || []),
            ...(contentTypes.practice || []),
            ...(contentTypes.assessment || [])
        ];
    }, [contentTypes]);


    // --- EditableSection Handlers (Unchanged) ---
    const handleEdit = (key, content) => setEditingState({ key, content });
    const handleContentChange = (content) => setEditingState(prev => ({ ...prev, content }));
    const handleCancel = () => setEditingState({ key: null, content: '' });

    const handleSave = () => {
        setLessonContent(prev => updateNestedState(prev, editingState.key, editingState.content));
        setAdaptedKeys(prev => new Set(prev).add(editingState.key));
        setEditingState({ key: null, content: '' });
    };

    const handleSaveAdaptations = async () => {
        if (!editingLessonId) {
            message.error("Cannot save: No lesson ID provided.");
            return;
        }

        message.loading({ content: 'Saving changes...', key: 'save' });
        try {
            await lessonPlanApi.updateLessonPlan(editingLessonId, lessonContent);
            message.success({ content: 'Adaptations saved!', key: 'save' });
            setAdaptedKeys(new Set());
        } catch (err) {
            message.error({ content: `Save failed: ${err.message}`, key: 'save' });
        }
    };

    // --- Segment & Block Management Handlers ---
    const showBlockLibrary = (index) => {
        setInsertionIndex(index);
        setIsBlockLibraryVisible(true);
    };

    const handleAddBlock = (block) => {
        const newSegment = {
            name: block.title,
            minutes: 10,
            goal: block.description || 'Custom activity',
            teacherMoves: ['Facilitate the new activity.'],
            studentActions: ['Engage in the activity.'],
            checksForUnderstanding: ['Monitor student participation.'],
            differentiation: null,
            materials: [],
            textbookResources: []
        };

        const updatedSegments = [...lessonContent.segments];
        updatedSegments.splice(insertionIndex, 0, newSegment);

        setLessonContent(prev => ({ ...prev, segments: updatedSegments }));
        setIsBlockLibraryVisible(false);
        setInsertionIndex(null);
        setAdaptedKeys(prev => new Set(prev).add(`segments.${insertionIndex}`));
        message.success(`"${block.title}" block added to your plan.`);
    };

    const handleRemoveSegment = (indexToRemove) => {
        Modal.confirm({
            title: 'Remove Segment?',
            content: `Are you sure you want to remove "${lessonContent.segments[indexToRemove].name}"?`,
            okText: 'Remove',
            okType: 'danger',
            onOk: () => {
                const updatedSegments = lessonContent.segments.filter((_, i) => i !== indexToRemove);
                setLessonContent(prev => ({ ...prev, segments: updatedSegments }));
                setAdaptedKeys(prev => new Set(prev).add(`segments`));
                message.success('Segment removed.');
            }
        });
    };

    const handleRemoveResource = (segmentIndex, resourceIndex) => {
        const resource = lessonContent.segments[segmentIndex].textbookResources[resourceIndex];
        Modal.confirm({
            title: 'Remove Resource?',
            content: `Are you sure you want to remove "${resource.displayTitle}" from this segment?`,
            okText: 'Remove',
            okType: 'danger',
            onOk: () => {
                const updatedSegments = [...lessonContent.segments];
                const segmentToUpdate = { ...updatedSegments[segmentIndex] };

                const updatedResources = segmentToUpdate.textbookResources.filter((_, i) => i !== resourceIndex);

                segmentToUpdate.textbookResources = updatedResources;
                updatedSegments[segmentIndex] = segmentToUpdate;

                setLessonContent(prev => ({ ...prev, segments: updatedSegments }));
                setAdaptedKeys(prev => new Set(prev).add(`segments.${segmentIndex}.textbookResources`));
                message.success('Resource removed.');
            }
        });
    };

    const handleAddListItem = (path, newItemTemplate, itemName = 'New item') => {
        const newLessonContent = JSON.parse(JSON.stringify(lessonContent));

        const keys = path.split('.');
        let current = newLessonContent;
        let parent = null;
        let lastKey = null;

        for (const key of keys) {
            if (current[key] === undefined || current[key] === null) {
                if (key === keys[keys.length - 1]) {
                    current[key] = [];
                } else {
                    current[key] = {};
                }
            }
            parent = current;
            current = current[key];
            lastKey = key;
        }

        if (!Array.isArray(current)) {
            console.error("Path does not lead to an array:", path);
            if (Array.isArray(parent[lastKey])) {
                current = parent[lastKey];
            } else {
                message.error("Could not add item: not an array.");
                return;
            }
        }

        const newItem = typeof newItemTemplate === 'function'
            ? newItemTemplate()
            : JSON.parse(JSON.stringify(newItemTemplate));

        current.push(newItem);

        setLessonContent(newLessonContent);
        setAdaptedKeys(prev => new Set(prev).add(path));
        message.success(`${itemName} added.`);
    };

    const handleRemoveListItem = (path, indexToRemove, itemText = 'this item') => {
        Modal.confirm({
            title: 'Remove Item?',
            content: `Are you sure you want to remove "${itemText.substring(0, 100)}${itemText.length > 100 ? '...' : ''}"?`,
            okText: 'Remove',
            okType: 'danger',
            onOk: () => {
                const newLessonContent = JSON.parse(JSON.stringify(lessonContent));

                const keys = path.split('.');
                let current = newLessonContent;

                for (let i = 0; i < keys.length; i++) {
                    const key = keys[i];
                    if (current[key] === undefined) {
                        console.error("Path not found for deletion:", path);
                        message.error("Could not remove item: path not found.");
                        return;
                    }
                    current = current[key];
                }

                if (!Array.isArray(current)) {
                    console.error("Path does not lead to an array:", path);
                    message.error("Could not remove item: not an array.");
                    return;
                }

                current.splice(indexToRemove, 1);

                setLessonContent(newLessonContent);
                setAdaptedKeys(prev => new Set(prev).add(path));
                message.success(`Item removed.`);
            }
        });
    };

    const handleClearField = (path, fieldName = 'this field') => {
        Modal.confirm({
            title: `Clear ${fieldName}?`,
            content: `Are you sure you want to clear the content for ${fieldName}?`,
            okText: 'Clear',
            okType: 'danger',
            onOk: () => {
                const newLessonContent = updateNestedState(lessonContent, path, null);

                setLessonContent(newLessonContent);
                setAdaptedKeys(prev => new Set(prev).add(path));
                message.success(`${fieldName} cleared.`);
            }
        });
    };


    const showSegmentEditModal = (index) => {
        setEditingSegmentIndex(index);
        segmentForm.setFieldsValue(lessonContent.segments[index]);
        setIsSegmentModalVisible(true);
    };

    const handleSegmentModalSave = () => {
        segmentForm.validateFields().then(values => {
            const updatedSegments = [...lessonContent.segments];
            updatedSegments[editingSegmentIndex] = {
                ...updatedSegments[editingSegmentIndex],
                ...values
            };
            setLessonContent(prev => ({ ...prev, segments: updatedSegments }));
            setIsSegmentModalVisible(false);
            setEditingSegmentIndex(null);
            setAdaptedKeys(prev => new Set(prev).add(`segments.${editingSegmentIndex}`));
            message.success('Segment updated.');
        });
    };

    const getResourceIcon = useCallback((type) => {
        const lowerType = type ? type.toLowerCase() : 'unknown';
        let mappedType = lowerType;

        switch(lowerType) {
            case 'practice_question':
                mappedType = 'quiz';
                break;
            case 'lesson_notes':
                mappedType = 'lesson_notes';
                break;
            case 'video':
                mappedType = 'video';
                break;
            case 'worksheet':
                mappedType = 'worksheet';
                break;
            case 'scorcher':
                mappedType = 'scorcher';
                break;
            case 'quiz':
                mappedType = 'quiz';
                break;
            case 'question':
                mappedType = 'quiz';
                break;
            default:
                mappedType = lowerType;
        }

        const resource = allContentTypes.find(t => t.value === mappedType);
        return resource ? React.cloneElement(getIcon(resource.icon), { style: { fontSize: '22px', color: '#1677ff' } }) :
            <FileTextOutlined style={{ fontSize: '22px', color: '#1677ff' }} />;
    }, [allContentTypes]);


    const resourceLabelMap = useMemo(() => {
        const map = new Map();
        allContentTypes.forEach(item => {
            map.set(item.value, item.label);
        });
        return map;
    }, [allContentTypes]);

    // --- useMemo hook (Unchanged) ---
    const configData = useMemo(() => {
        if (!formValues || Object.keys(formValues).length === 0) return [];

        const data = [
            { title: 'Learning Goal', value: formValues.learningGoal },
            { title: 'Knowledge Dimension', value: formValues.knowledgeDimension },
            { title: 'Timeframe', value: formValues.timeframe },
            { title: 'DOK Level', value: formValues.dokLevel },
            { title: 'Delivery Mode', value: formValues.classroomFormat },
            { title: 'Resource Profile', value: formValues.resourceProfile },
            { title: 'Assessment', value: formValues.assessmentFitCategory },
            { title: 'Well-being Focus', value: formValues.wellBeingFocus },
            { title: 'Lesson Plan Template', value: formValues.lessonPlanTemplateName || <Text code>{formValues.lessonPlanTemplateId}</Text> },
        ].filter(item => item.value);

        if (formValues.syllabusName || formValues.topicName || formValues.conceptName) {
            data.unshift({
                title: 'Aligned Textbook Section',
                value: (
                    <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                        {formValues.syllabusName && (
                            <Text><BookOutlined /> {formValues.syllabusName}</Text>
                        )}
                        {formValues.topicName && (
                            <Text>{formValues.topicName}</Text>
                        )}
                        {formValues.conceptName && (
                            <Text>{formValues.conceptName}</Text>
                        )}
                    </Space>
                )
            });
        }

        if (formValues.selectedResources?.length > 0) {
            data.push({
                title: 'Selected Resources',
                value: (
                    <Space wrap>
                        {formValues.selectedResources.map(res => (
                            <Tag key={res} icon={React.cloneElement(getResourceIcon(res), {style: {fontSize: '14px', marginRight: '4px'}})}>
                                {resourceLabelMap.get(res) || formatConfigKey(res)}
                            </Tag>
                        ))}
                    </Space>
                )
            });
        }

        if (formValues.lessonPlanComponents?.length > 0) {
            data.push({
                title: 'Lesson Components',
                value: (
                    <Space wrap>
                        {formValues.lessonPlanComponents.map(comp => (
                            <Tag key={comp} color="blue">{formatConfigKey(comp)}</Tag>
                        ))}
                    </Space>
                )
            });
        }

        return data;
    }, [formValues, resourceLabelMap, getResourceIcon]);


    const showComponents = useMemo(() => {
        const components = new Set(formValues?.lessonPlanComponents || []);
        if (lessonContent?.exitQuizQuestions?.length > 0) {
            components.add('exit_quiz');
        }
        return components;
    }, [formValues, lessonContent]);


    if (!lessonContent || !lessonContent.segments) {
        return <Card style={{ minHeight: 400 }}><Spin description="Loading lesson preview..." /></Card>;
    }

    const showPlanningBlock = (
        showComponents.has('corrective_plan') ||
        showComponents.has('pitfalls_indicators') ||
        showComponents.has('planning_notes')
    );

    const anchorLinks = [];
    if (configData.length > 0) {
        anchorLinks.push({ key: 'config', href: '#lesson-configuration', title: 'Lesson Plan Options' });
    }
    anchorLinks.push(
        { key: 'objectives', href: '#lesson-objectives', title: 'Learning Objectives' },
        { key: 'plan', href: '#lesson-plan', title: 'Instructional Plan' }
    );

    if (showComponents.has('exit_quiz')) {
        anchorLinks.push({ key: 'exitquiz', href: '#lesson-exit-quiz', title: 'Exit Quiz' });
    }

    if (showComponents.has('overview')) {
        anchorLinks.push({ key: 'diff', href: '#lesson-differentiation', title: 'Differentiation' });
    }
    if (showComponents.has('rubric')) {
        anchorLinks.push({ key: 'rubric', href: '#lesson-rubric', title: 'Proficiency Rubric' });
    }
    if (showComponents.has('probing_questions')) {
        anchorLinks.push({ key: 'qs', href: '#lesson-questions', title: 'Probing Questions' });
    }
    if (showComponents.has('language_scaffolds')) {
        anchorLinks.push({ key: 'lang', href: '#lesson-language', title: 'Language Scaffolds' });
    }
    if (showPlanningBlock) {
        anchorLinks.push({ key: 'planning', href: '#lesson-planning', title: 'Planning Considerations' });
    }

    // --- Rubric Columns (Unchanged from last update) ---
    const rubricColumns = [
        {
            title: 'Level',
            dataIndex: 'levelName',
            key: 'levelName',
            width: '25%',
            render: (text, record, index) => {
                const contentKey = `proficiencyRubric.levels.${index}.levelName`;
                const isEditingThisOne = editingState.key === contentKey;
                return (
                    <EditableSection
                        contentKey={contentKey}
                        editContent={isEditingThisOne ? editingState.content : text}
                        isEditing={isEditingThisOne}
                        isAdapted={adaptedKeys.has(contentKey)}
                        onEdit={handleEdit}
                        onChange={handleContentChange}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        component="span"
                    >
                        <Text strong>{text}</Text>
                    </EditableSection>
                );
            }
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text, record, index) => {
                const contentKey = `proficiencyRubric.levels.${index}.description`;
                const isEditingThisOne = editingState.key === contentKey;
                return (
                    <EditableSection
                        contentKey={contentKey}
                        editContent={isEditingThisOne ? editingState.content : text}
                        isEditing={isEditingThisOne}
                        isAdapted={adaptedKeys.has(contentKey)}
                        onEdit={handleEdit}
                        onChange={handleContentChange}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        component="span"
                    >
                        <Text>{text}</Text>
                    </EditableSection>
                );
            }
        },
        {
            title: 'Action',
            key: 'action',
            width: '50px',
            render: (text, record, index) => (
                <Tooltip title="Remove level">
                    <Button
                        type="text"
                        danger
                        shape="circle"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveListItem(
                            'proficiencyRubric.levels',
                            index,
                            record.levelName || 'this level'
                        )}
                    />
                </Tooltip>
            )
        },
    ];

    const formatDifferentiation = (level) => {
        if (level == null) return 'N/A';
        if (level === 0 || level === '0') return 'Standard';
        return level;
    };


    return (
        <Row gutter={24}>
            <Col sm={24} md={19}>
                {/* --- Modals (Unchanged) --- */}
                <Modal
                    title={<Space><AppstoreAddOutlined />Instructional Block Library</Space>}
                    open={isBlockLibraryVisible}
                    onCancel={() => setIsBlockLibraryVisible(false)}
                    footer={null}
                    width={800}
                >
                    <List
                        itemLayout="horizontal"
                        dataSource={instructionalBlocks}
                        renderItem={block => (
                            <List.Item actions={[<Button type="primary" onClick={() => handleAddBlock(block)}>Add Block</Button>]}>
                                <List.Item.Meta
                                    key={block.key}
                                    avatar={React.cloneElement(getIcon(block.icon), {style: {fontSize: 24}})}
                                    title={block.title}
                                    description={block.description}
                                />
                            </List.Item>
                        )}
                    />
                </Modal>
                <Modal
                    title="Edit Segment"
                    open={isSegmentModalVisible}
                    onOk={handleSegmentModalSave}
                    onCancel={() => setIsSegmentModalVisible(false)}
                    destroyOnClose
                >
                    <Form form={segmentForm} layout="vertical" initialValues={{ name: '', minutes: 10, goal: '' }}>
                        <Form.Item name="name" label="Segment Name" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="minutes" label="Minutes" rules={[{ required: true, type: 'number' }]}>
                            <InputNumber min={1} max={180} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="goal" label="Goal">
                            <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
                        </Form.Item>
                    </Form>
                </Modal>

                <Space orientation="vertical" style={{ width: '100%' }} size="large">
                    {configData.length > 0 && (
                        <div id="lesson-configuration">
                            <Card>
                                <Title level={4} style={{ marginTop: 0 }}><ToolOutlined /> Lesson Plan Options</Title>
                                <List
                                    grid={{
                                        gutter: 16,
                                        xs: 1,
                                        sm: 2,
                                        md: 2,
                                        lg: 2,
                                        xl: 2,
                                        xxl: 2,
                                    }}
                                    dataSource={configData}
                                    renderItem={item => (
                                        <List.Item>
                                            <List.Item.Meta
                                                title={<Text strong>{item.title}</Text>}
                                                description={typeof item.value === 'string' ? <Text>{formatConfigKey(item.value)}</Text> : item.value}
                                            />
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </div>
                    )}

                    {(formValues?.customInstructions || lessonContent?.customInstructions) && (
                        <Alert
                            message={<Title level={4} style={{ margin: 0 }}>Teacher's Note</Title>}
                            description={<div dangerouslySetInnerHTML={{ __html: formValues?.customInstructions || lessonContent?.customInstructions }} />}
                            type="info" showIcon icon={<InfoCircleOutlined />}
                        />
                    )}

                    {/* --- Objectives Card (MODIFIED) --- */}
                    <div id="lesson-objectives">
                        <Title level={2} style={{ marginBottom: 0 }}>{lessonContent.contentName}</Title>
                        <Title level={4} type="secondary" style={{ marginTop: 0, marginBottom: '24px' }}>
                            {lessonContent.lessonName}
                        </Title>
                        <Card size="small">
                            <Row justify="space-between" align="middle" style={{marginBottom: '8px'}}>
                                <Title level={4} style={{marginTop: 0, marginBottom: 0}}><AimOutlined /> Learning Objectives</Title>
                                {/* --- START: MODIFIED BUTTON --- */}
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    size="small"
                                    onClick={() => handleAddListItem('objectives', 'New objective. Click to edit.', 'Objective')}
                                >
                                    Add Objective
                                </Button>
                                {/* --- END: MODIFIED BUTTON --- */}
                            </Row>
                            <List
                                size="small"
                                dataSource={lessonContent.objectives}
                                renderItem={(item, itemIndex) => (
                                    <List.Item>
                                        {(() => {
                                            const isEditingThisOne = editingState.key === `objectives.${itemIndex}`;
                                            return (
                                                <EditableSection
                                                    contentKey={`objectives.${itemIndex}`}
                                                    editContent={isEditingThisOne ? editingState.content : item}
                                                    isEditing={isEditingThisOne}
                                                    isAdapted={adaptedKeys.has(`objectives.${itemIndex}`)}
                                                    onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                                    onDelete={() => handleRemoveListItem('objectives', itemIndex, item)}
                                                    deleteTooltip="Remove objective"
                                                >
                                                    <Text>{item}</Text>
                                                </EditableSection>
                                            );
                                        })()}
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </div>

                    <div id="lesson-plan">
                        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
                            <Col>
                                <Title level={2} style={{ margin: 0 }}>Instructional Plan</Title>
                            </Col>
                            <Col>
                                <Statistic
                                    title="Total Time"
                                    value={lessonContent.minutes}
                                    suffix="min"
                                    prefix={<ClockCircleOutlined />}
                                />
                            </Col>
                        </Row>
                        <Collapse defaultActiveKey={[0]} expandIconPosition="end">
                            {lessonContent.segments.map((segment, segmentIndex) => (
                                <React.Fragment key={segment.key || segmentIndex}>
                                    <Panel
                                        header={<Space><Avatar style={{ backgroundColor: '#1677ff' }}>{segmentIndex + 1}</Avatar><Title level={5} style={{ margin: 0 }}>{segment.name}</Title></Space>}
                                        key={segmentIndex}
                                        extra={
                                            <Space>
                                                <Tag color={segment.isCustomBlock ? "purple" : "blue"}>{segment.minutes} min</Tag>
                                                <Button
                                                    type="text"
                                                    shape="circle"
                                                    icon={<EditOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        showSegmentEditModal(segmentIndex);
                                                    }}
                                                />
                                                <Button
                                                    type="text"
                                                    danger
                                                    shape="circle"
                                                    icon={<DeleteOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveSegment(segmentIndex);
                                                    }}
                                                />
                                            </Space>
                                        }
                                    >
                                        <Paragraph><strong>Goal:</strong> {segment.goal}</Paragraph>
                                        <Space orientation="vertical" style={{ width: '100%' }} size="middle">

                                            {/* --- Teacher Moves (MODIFIED) --- */}
                                            {segment.teacherMoves && segment.teacherMoves.length > 0 && (
                                                <>
                                                    <Row justify="space-between" align="middle">
                                                        <Title level={5} style={{ color: '#1677ff', margin: 0 }}>Teacher Moves</Title>
                                                        {/* --- START: MODIFIED BUTTON --- */}
                                                        <Button
                                                            type="primary"
                                                            icon={<PlusOutlined />}
                                                            size="small"
                                                            onClick={() => handleAddListItem(`segments.${segmentIndex}.teacherMoves`, 'New move. Click to edit.', 'Teacher Move')}
                                                        >
                                                            Add Move
                                                        </Button>
                                                        {/* --- END: MODIFIED BUTTON --- */}
                                                    </Row>
                                                    <List size="small" dataSource={segment.teacherMoves} renderItem={(item, itemIndex) => (
                                                        <List.Item>
                                                            {(() => {
                                                                const isEditingThisOne = editingState.key === `segments.${segmentIndex}.teacherMoves.${itemIndex}`;
                                                                return (
                                                                    <EditableSection
                                                                        contentKey={`segments.${segmentIndex}.teacherMoves.${itemIndex}`}
                                                                        editContent={isEditingThisOne ? editingState.content : item}
                                                                        isEditing={isEditingThisOne}
                                                                        isAdapted={adaptedKeys.has(`segments.${segmentIndex}.teacherMoves.${itemIndex}`)}
                                                                        onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                                                        onDelete={() => handleRemoveListItem(`segments.${segmentIndex}.teacherMoves`, itemIndex, item)}
                                                                        deleteTooltip="Remove move"
                                                                    >
                                                                        <Text>{item}</Text>
                                                                    </EditableSection>
                                                                );
                                                            })()}
                                                        </List.Item>
                                                    )} />
                                                </>
                                            )}

                                            {/* --- Student Actions (MODIFIED) --- */}
                                            {segment.studentActions && segment.studentActions.length > 0 && (
                                                <>
                                                    <Row justify="space-between" align="middle">
                                                        <Title level={5} style={{ color: '#1677ff', margin: 0 }}>Student Actions</Title>
                                                        {/* --- START: MODIFIED BUTTON --- */}
                                                        <Button
                                                            type="primary"
                                                            icon={<PlusOutlined />}
                                                            size="small"
                                                            onClick={() => handleAddListItem(`segments.${segmentIndex}.studentActions`, 'New action. Click to edit.', 'Student Action')}
                                                        >
                                                            Add Action
                                                        </Button>
                                                        {/* --- END: MODIFIED BUTTON --- */}
                                                    </Row>
                                                    <List size="small" dataSource={segment.studentActions} renderItem={(item, itemIndex) => (
                                                        <List.Item>
                                                            {(() => {
                                                                const isEditingThisOne = editingState.key === `segments.${segmentIndex}.studentActions.${itemIndex}`;
                                                                return (
                                                                    <EditableSection
                                                                        contentKey={`segments.${segmentIndex}.studentActions.${itemIndex}`}
                                                                        editContent={isEditingThisOne ? editingState.content : item}
                                                                        isEditing={isEditingThisOne}
                                                                        isAdapted={adaptedKeys.has(`segments.${segmentIndex}.studentActions.${itemIndex}`)}
                                                                        onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                                                        onDelete={() => handleRemoveListItem(`segments.${segmentIndex}.studentActions`, itemIndex, item)}
                                                                        deleteTooltip="Remove action"
                                                                    >
                                                                        <Text>{item}</Text>
                                                                    </EditableSection>
                                                                );
                                                            })()}
                                                        </List.Item>
                                                    )} />
                                                </>
                                            )}

                                            {/* --- Checks for Understanding (MODIFIED) --- */}
                                            {segment.checksForUnderstanding && segment.checksForUnderstanding.length > 0 && (
                                                <>
                                                    <Row justify="space-between" align="middle">
                                                        <Title level={5} style={{ color: '#1677ff', margin: 0 }}>Checks for Understanding</Title>
                                                        {/* --- START: MODIFIED BUTTON --- */}
                                                        <Button
                                                            type="primary"
                                                            icon={<PlusOutlined />}
                                                            size="small"
                                                            onClick={() => handleAddListItem(`segments.${segmentIndex}.checksForUnderstanding`, 'New check. Click to edit.', 'Check for Understanding')}
                                                        >
                                                            Add Check
                                                        </Button>
                                                        {/* --- END: MODIFIED BUTTON --- */}
                                                    </Row>
                                                    <List size="small" dataSource={segment.checksForUnderstanding} renderItem={(item, itemIndex) => (
                                                        <List.Item>
                                                            {(() => {
                                                                const isEditingThisOne = editingState.key === `segments.${segmentIndex}.checksForUnderstanding.${itemIndex}`;
                                                                return (
                                                                    <EditableSection
                                                                        contentKey={`segments.${segmentIndex}.checksForUnderstanding.${itemIndex}`}
                                                                        editContent={isEditingThisOne ? editingState.content : item}
                                                                        isEditing={isEditingThisOne}
                                                                        isAdapted={adaptedKeys.has(`segments.${segmentIndex}.checksForUnderstanding.${itemIndex}`)}
                                                                        onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                                                        onDelete={() => handleRemoveListItem(`segments.${segmentIndex}.checksForUnderstanding`, itemIndex, item)}
                                                                        deleteTooltip="Remove check"
                                                                    >
                                                                        <Text>{item}</Text>
                                                                    </EditableSection>
                                                                );
                                                            })()}
                                                        </List.Item>
                                                    )} />
                                                </>
                                            )}

                                            {/* --- Materials (MODIFIED) --- */}
                                            {segment.materials && segment.materials.length > 0 && (
                                                <>
                                                    <Row justify="space-between" align="middle">
                                                        <Title level={5} style={{ color: '#1677ff', marginTop: '16px', marginBottom: 0 }}>Materials</Title>
                                                        {/* --- START: MODIFIED BUTTON --- */}
                                                        <Button
                                                            type="primary"
                                                            icon={<PlusOutlined />}
                                                            size="small"
                                                            onClick={() => handleAddListItem(`segments.${segmentIndex}.materials`, 'New material. Click to edit.', 'Material')}
                                                        >
                                                            Add Material
                                                        </Button>
                                                        {/* --- END: MODIFIED BUTTON --- */}
                                                    </Row>
                                                    <List size="small" dataSource={segment.materials} renderItem={(item, itemIndex) => (
                                                        <List.Item>
                                                            {(() => {
                                                                const isEditingThisOne = editingState.key === `segments.${segmentIndex}.materials.${itemIndex}`;
                                                                return (
                                                                    <EditableSection
                                                                        contentKey={`segments.${segmentIndex}.materials.${itemIndex}`}
                                                                        editContent={isEditingThisOne ? editingState.content : item}
                                                                        isEditing={isEditingThisOne}
                                                                        isAdapted={adaptedKeys.has(`segments.${segmentIndex}.materials.${itemIndex}`)}
                                                                        onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                                                        onDelete={() => handleRemoveListItem(`segments.${segmentIndex}.materials`, itemIndex, item)}
                                                                        deleteTooltip="Remove material"
                                                                    >
                                                                        <Text>{item}</Text>
                                                                    </EditableSection>
                                                                );
                                                            })()}
                                                        </List.Item>
                                                    )} />
                                                </>
                                            )}

                                            {segment.textbookResources && segment.textbookResources.length > 0 && (
                                                <>
                                                    <Title level={5} style={{ color: '#1677ff', marginTop: '16px' }}>Aligned Resources</Title>
                                                    <List
                                                        size="small"
                                                        dataSource={segment.textbookResources}
                                                        renderItem={(resource, resourceIndex) => (
                                                            <List.Item
                                                                actions={[
                                                                    <Tooltip title="Remove resource">
                                                                        <Button
                                                                            type="text"
                                                                            danger
                                                                            shape="circle"
                                                                            icon={<DeleteOutlined />}
                                                                            onClick={() => handleRemoveResource(segmentIndex, resourceIndex)}
                                                                        />
                                                                    </Tooltip>
                                                                ]}
                                                            >
                                                                <List.Item.Meta
                                                                    avatar={getResourceIcon(resource.contentType)}
                                                                    title={<a href={`/resource/${resource.documentId}`} target="_blank" rel="noopener noreferrer">{resource.displayTitle}</a>}
                                                                    description={resource.contentType.replace(/_/g, ' ')}
                                                                />
                                                            </List.Item>
                                                        )}
                                                    />
                                                </>
                                            )}
                                        </Space>
                                    </Panel>
                                    <div style={{ margin: '8px 0', textAlign: 'center' }}>
                                        <Button
                                            type="dashed"
                                            shape="circle"
                                            icon={<PlusOutlined />}
                                            title="Add instructional block here"
                                            onClick={() => showBlockLibrary(segmentIndex + 1)}
                                        />
                                    </div>
                                </React.Fragment>
                            ))}
                        </Collapse>
                    </div>

                    {showComponents.has('exit_quiz') && (
                        <div id="lesson-exit-quiz">
                            <Title level={2}>Exit Quiz</Title>
                            <Card size="small" icon={<CheckSquareOutlined />}>
                                <List
                                    dataSource={lessonContent.exitQuizQuestions || []}
                                    renderItem={(resource, resourceIndex) => (
                                        <List.Item
                                            key={resource.documentId || resourceIndex}
                                            actions={[
                                                <Tooltip title="Remove question">
                                                    <Button
                                                        type="text"
                                                        danger
                                                        shape="circle"
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => handleRemoveListItem(
                                                            'exitQuizQuestions',
                                                            resourceIndex,
                                                            resource.displayTitle || 'this question'
                                                        )}
                                                    />
                                                </Tooltip>
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={getResourceIcon(resource.contentType)}
                                                title={resource.displayTitle}
                                                description={resource.contentSummary}
                                            />
                                        </List.Item>
                                    )}
                                />
                                {(lessonContent.exitQuizQuestions == null || lessonContent.exitQuizQuestions.length === 0) && (
                                    <Text type="secondary" italic>No exit quiz questions were generated for this lesson.</Text>
                                )}
                            </Card>
                        </div>
                    )}

                    {showComponents.has('overview') && (
                        <div id="lesson-differentiation">
                            <Title level={2}>Differentiation Overview</Title>
                            <Card size="small" icon={<TeamOutlined />}>
                                <EditableSection
                                    contentKey="differentiationBrief"
                                    editContent={editingState.key === 'differentiationBrief' ? editingState.content : lessonContent.differentiationBrief}
                                    isEditing={editingState.key === 'differentiationBrief'}
                                    isAdapted={adaptedKeys.has('differentiationBrief')}
                                    onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                    onDelete={() => handleClearField('differentiationBrief', 'Differentiation Overview')}
                                    deleteTooltip="Clear overview"
                                >
                                    <Text>{lessonContent.differentiationBrief || <Text type="secondary" italic>Not specified</Text>}</Text>
                                </EditableSection>
                            </Card>
                        </div>
                    )}

                    {showComponents.has('rubric') && (
                        <div id="lesson-rubric">
                            <Row justify="space-between" align="middle">
                                <Title level={2} style={{margin: 0}}>Proficiency Rubric</Title>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => handleAddListItem(
                                        'proficiencyRubric.levels',
                                        () => ({ levelName: `New Level ${lessonContent.proficiencyRubric?.levels?.length + 1 || 1}`, description: 'Click to edit description' }),
                                        'Rubric Level'
                                    )}
                                    title="Add Rubric Level"
                                >
                                    Add Level
                                </Button>
                            </Row>
                            {lessonContent.proficiencyRubric && (
                                <Card size="small" style={{ margin: "16px 0", background: '#fafafa' }}>
                                    <EditableSection
                                        contentKey='proficiencyRubric.assessedItem'
                                        editContent={editingState.key === 'proficiencyRubric.assessedItem' ? editingState.content : lessonContent.proficiencyRubric.assessedItem}
                                        isEditing={editingState.key === 'proficiencyRubric.assessedItem'}
                                        isAdapted={adaptedKeys.has('proficiencyRubric.assessedItem')}
                                        onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                        onDelete={() => handleClearField('proficiencyRubric.assessedItem', 'Assessed Item')}
                                        deleteTooltip="Clear assessed item"
                                    >
                                        <Paragraph style={{ margin: 0 }}>
                                            <strong>Assessed Item:</strong> {lessonContent.proficiencyRubric.assessedItem || <Text type="secondary" italic>Not specified</Text>}
                                        </Paragraph>
                                    </EditableSection>
                                </Card>
                            )}

                            <Table
                                columns={rubricColumns}
                                dataSource={lessonContent.proficiencyRubric?.levels || []}
                                pagination={false}
                                bordered
                                size="small"
                                rowKey="levelName"
                            />
                        </div>
                    )}

                    {/* --- Key Probing Questions (MODIFIED) --- */}
                    {showComponents.has('probing_questions') && (
                        <div id="lesson-questions">
                            <Title level={2}>Key Probing Questions</Title>
                            <Card size="small" icon={<BulbOutlined />}>
                                <Row justify="space-between" align="middle" style={{marginBottom: 8}}>
                                    <Text strong>Questions</Text>
                                    {/* --- START: MODIFIED BUTTON --- */}
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        size="small"
                                        onClick={() => handleAddListItem('keyProbingQuestions', 'New question. Click to edit.', 'Question')}
                                    >
                                        Add Question
                                    </Button>
                                    {/* --- END: MODIFIED BUTTON --- */}
                                </Row>
                                <List
                                    dataSource={lessonContent.keyProbingQuestions || []}
                                    renderItem={(item, itemIndex) => (
                                        <List.Item key={itemIndex}>
                                            {(() => {
                                                const contentKey = `keyProbingQuestions.${itemIndex}`;
                                                const isEditingThisOne = editingState.key === contentKey;
                                                return (
                                                    <EditableSection
                                                        contentKey={contentKey}
                                                        editContent={isEditingThisOne ? editingState.content : item}
                                                        isEditing={isEditingThisOne}
                                                        isAdapted={adaptedKeys.has(contentKey)}
                                                        onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                                        onDelete={() => handleRemoveListItem('keyProbingQuestions', itemIndex, item)}
                                                        deleteTooltip="Remove question"
                                                    >
                                                        <Text>{item}</Text>
                                                    </EditableSection>
                                                );
                                            })()}
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </div>
                    )}

                    {/* --- Academic Language Scaffolds (MODIFIED) --- */}
                    {showComponents.has('language_scaffolds') && (
                        <div id="lesson-language">
                            <Title level={2}>Academic Language Scaffolds</Title>
                            <Card size="small" icon={<MessageOutlined />}>
                                {lessonContent.academicLanguageScaffolds && (
                                    <EditableSection
                                        contentKey='academicLanguageScaffolds.discourseType'
                                        editContent={editingState.key === 'academicLanguageScaffolds.discourseType' ? editingState.content : lessonContent.academicLanguageScaffolds.discourseType}
                                        isEditing={editingState.key === 'academicLanguageScaffolds.discourseType'}
                                        isAdapted={adaptedKeys.has('academicLanguageScaffolds.discourseType')}
                                        onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                        onDelete={() => handleClearField('academicLanguageScaffolds.discourseType', 'Discourse Type')}
                                        deleteTooltip="Clear discourse type"
                                    >
                                        <Paragraph><strong>Discourse Type:</strong> {lessonContent.academicLanguageScaffolds.discourseType || <Text type="secondary" italic>Not specified</Text>}</Paragraph>
                                    </EditableSection>
                                )}
                                <Row justify="space-between" align="middle" style={{marginTop: 8, marginBottom: 8}}>
                                    <Title level={5} style={{marginTop: 0, marginBottom: 0}}>Sentence Stems</Title>
                                    {/* --- START: MODIFIED BUTTON --- */}
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        size="small"
                                        onClick={() => handleAddListItem('academicLanguageScaffolds.stems', 'New stem. Click to edit.', 'Sentence Stem')}
                                    >
                                        Add Stem
                                    </Button>
                                    {/* --- END: MODIFIED BUTTON --- */}
                                </Row>
                                <List
                                    dataSource={lessonContent.academicLanguageScaffolds?.stems || []}
                                    renderItem={(item, itemIndex) => (
                                        <List.Item key={itemIndex}>
                                            {(() => {
                                                const contentKey = `academicLanguageScaffolds.stems.${itemIndex}`;
                                                const isEditingThisOne = editingState.key === contentKey;
                                                return (
                                                    <EditableSection
                                                        contentKey={contentKey}
                                                        editContent={isEditingThisOne ? editingState.content : item}
                                                        isEditing={isEditingThisOne}
                                                        isAdapted={adaptedKeys.has(contentKey)}
                                                        onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                                        onDelete={() => handleRemoveListItem('academicLanguageScaffolds.stems', itemIndex, item)}
                                                        deleteTooltip="Remove stem"
                                                    >
                                                        <Text code>{item}</Text>
                                                    </EditableSection>
                                                );
                                            })()}
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </div>
                    )}

                    {/* --- Planning Considerations (Unchanged from last update) --- */}
                    {showPlanningBlock && (
                        <div id="lesson-planning">
                            <Title level={2}>Planning Considerations</Title>
                            <Collapse defaultActiveKey={['1']}>
                                {showComponents.has('corrective_plan') && (
                                    <Panel header={<Space><WarningOutlined />Corrective Instruction Plan</Space>} key="1">
                                        <EditableSection
                                            contentKey='correctiveInstructionPlan.misconception'
                                            editContent={editingState.key === 'correctiveInstructionPlan.misconception' ? editingState.content : lessonContent.correctiveInstructionPlan?.misconception}
                                            isEditing={editingState.key === 'correctiveInstructionPlan.misconception'}
                                            isAdapted={adaptedKeys.has('correctiveInstructionPlan.misconception')}
                                            onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                            onDelete={() => handleClearField('correctiveInstructionPlan.misconception', 'Common Misconception')}
                                            deleteTooltip="Clear misconception"
                                        >
                                            <Paragraph><strong>Common Misconception:</strong> {lessonContent.correctiveInstructionPlan?.misconception || <Text type="secondary" italic>Not specified</Text>}</Paragraph>
                                        </EditableSection>
                                        <EditableSection
                                            contentKey='correctiveInstructionPlan.remediation'
                                            editContent={editingState.key === 'correctiveInstructionPlan.remediation' ? editingState.content : lessonContent.correctiveInstructionPlan?.remediation}
                                            isEditing={editingState.key === 'correctiveInstructionPlan.remediation'}
                                            isAdapted={adaptedKeys.has('correctiveInstructionPlan.remediation')}
                                            onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                            onDelete={() => handleClearField('correctiveInstructionPlan.remediation', 'Remediation Plan')}
                                            deleteTooltip="Clear remediation plan"
                                        >
                                            <Paragraph><strong>Remediation Plan:</strong> {lessonContent.correctiveInstructionPlan?.remediation || <Text type="secondary" italic>Not specified</Text>}</Paragraph>
                                        </EditableSection>
                                    </Panel>
                                )}
                                {showComponents.has('pitfalls_indicators') && (
                                    <>
                                        <Panel
                                            header={<Space><WarningOutlined />Common Pitfalls</Space>}
                                            key="2"
                                            extra={
                                                <Button
                                                    type="primary"
                                                    shape="circle"
                                                    icon={<PlusOutlined />}
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAddListItem('commonPitfalls', 'New pitfall. Click to edit.', 'Pitfall');
                                                    }}
                                                    title="Add Pitfall"
                                                />
                                            }
                                        >
                                            <List
                                                dataSource={lessonContent.commonPitfalls || []}
                                                renderItem={(item, itemIndex) => (
                                                    <List.Item key={itemIndex}>
                                                        {(() => {
                                                            const contentKey = `commonPitfalls.${itemIndex}`;
                                                            const isEditingThisOne = editingState.key === contentKey;
                                                            return (
                                                                <EditableSection
                                                                    contentKey={contentKey}
                                                                    editContent={isEditingThisOne ? editingState.content : item}
                                                                    isEditing={isEditingThisOne}
                                                                    isAdapted={adaptedKeys.has(contentKey)}
                                                                    onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                                                    onDelete={() => handleRemoveListItem('commonPitfalls', itemIndex, item)}
                                                                    deleteTooltip="Remove pitfall"
                                                                >
                                                                    <Text>{item}</Text>
                                                                </EditableSection>
                                                            );
                                                        })()}
                                                    </List.Item>
                                                )}
                                            />
                                        </Panel>
                                        <Panel
                                            header={<Space><CheckSquareOutlined />Success Indicators</Space>}
                                            key="3"
                                            extra={
                                                <Button
                                                    type="primary"
                                                    shape="circle"
                                                    icon={<PlusOutlined />}
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAddListItem('successIndicators', 'New indicator. Click to edit.', 'Indicator');
                                                    }}
                                                    title="Add Indicator"
                                                />
                                            }
                                        >
                                            <List
                                                dataSource={lessonContent.successIndicators || []}
                                                renderItem={(item, itemIndex) => (
                                                    <List.Item key={itemIndex}>
                                                        {(() => {
                                                            const contentKey = `successIndicators.${itemIndex}`;
                                                            const isEditingThisOne = editingState.key === contentKey;
                                                            return (
                                                                <EditableSection
                                                                    contentKey={contentKey}
                                                                    editContent={isEditingThisOne ? editingState.content : item}
                                                                    isEditing={isEditingThisOne}
                                                                    isAdapted={adaptedKeys.has(contentKey)}
                                                                    onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                                                    onDelete={() => handleRemoveListItem('successIndicators', itemIndex, item)}
                                                                    deleteTooltip="Remove indicator"
                                                                >
                                                                    <Text>{item}</Text>
                                                                </EditableSection>
                                                            );
                                                        })()}
                                                    </List.Item>
                                                )}
                                            />
                                        </Panel>
                                    </>
                                )}
                                {showComponents.has('planning_notes') && (
                                    <Panel
                                        header={<Space><ToolOutlined />Adaptation Notes</Space>}
                                        key="4"
                                        extra={
                                            <Button
                                                type="primary"
                                                shape="circle"
                                                icon={<PlusOutlined />}
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAddListItem('adaptationNotes', 'New note. Click to edit.', 'Note');
                                                }}
                                                title="Add Note"
                                            />
                                        }
                                    >
                                        <List
                                            dataSource={lessonContent.adaptationNotes || []}
                                            renderItem={(item, itemIndex) => (
                                                <List.Item key={itemIndex}>
                                                    {(() => {
                                                        const contentKey = `adaptationNotes.${itemIndex}`;
                                                        const isEditingThisOne = editingState.key === contentKey;
                                                        return (
                                                            <EditableSection
                                                                contentKey={contentKey}
                                                                editContent={isEditingThisOne ? editingState.content : item}
                                                                isEditing={isEditingThisOne}
                                                                isAdapted={adaptedKeys.has(contentKey)}
                                                                onEdit={handleEdit} onChange={handleContentChange} onSave={handleSave} onCancel={handleCancel}
                                                                onDelete={() => handleRemoveListItem('adaptationNotes', itemIndex, item)}
                                                                deleteTooltip="Remove note"
                                                            >
                                                                <Text>{item}</Text>
                                                            </EditableSection>
                                                        );
                                                    })()}
                                                </List.Item>
                                            )}
                                        />
                                    </Panel>
                                )}
                            </Collapse>
                        </div>
                    )}

                </Space>
            </Col>

            {/* --- Sticky Column (Unchanged) --- */}
            <Col sm={24} md={5}>
                <div style={{ position: 'sticky', top: '24px' }}>
                    <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        block
                        size="large"
                        onClick={handleSaveAdaptations}
                        disabled={!editingLessonId || adaptedKeys.size === 0}
                        style={{ marginBottom: '16px' }}
                    >
                        Save Adaptations
                    </Button>

                    <Card title="On This Page" size="small">
                        <Anchor items={anchorLinks} targetOffset={20} getContainer={getScrollContainer}/>
                    </Card>
                </div>
            </Col>
        </Row>
    );
};

export default InteractiveLessonPlan;